import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
} from '@mui/material';
import {
  LocationOn,
  Directions,
  Phone,
  WhatsApp,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';

// Google Maps type declarations
declare global {
  interface Window {
    google: any;
  }
}

interface MapProps {
  height?: string | number;
  showControls?: boolean;
}

const Map: React.FC<MapProps> = ({ 
  height = 500, 
  showControls = true 
}) => {
  const { theme: customTheme } = useTheme();
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Office location - moved outside to avoid dependency issues
  const officeLocation = React.useMemo(() => ({
    lat: 12.9141, // HSR Layout, Bangalore
    lng: 77.6412,
    address: '36/A, 3rd Cross 24th Main, Near Ranga Residency, HSR Layout 2nd Sector, Bengaluru 102'
  }), []);

  useEffect(() => {
    // Load Google Maps script
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      // For demo purposes, we'll use a placeholder
      // In production, replace YOUR_API_KEY with actual Google Maps API key
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY';
      
      if (apiKey === 'YOUR_API_KEY') {
        // Show placeholder map for demo
        setMapLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeMap;
      script.onerror = () => {
        console.warn('Google Maps failed to load, showing placeholder');
        setMapLoaded(true);
      };
      document.head.appendChild(script);
    };

    const initializeMap = () => {
      if (!mapRef.current) return;

      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 15,
        center: officeLocation,
        styles: [
          {
            featureType: 'all',
            elementType: 'geometry.fill',
            stylers: [{ weight: '2.00' }]
          },
          {
            featureType: 'all',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#9c9c9c' }]
          },
          {
            featureType: 'all',
            elementType: 'labels.text',
            stylers: [{ visibility: 'on' }]
          },
          {
            featureType: 'landscape',
            elementType: 'all',
            stylers: [{ color: '#f2f2f2' }]
          },
          {
            featureType: 'landscape.man_made',
            elementType: 'geometry',
            stylers: [{ color: '#f2f2f2' }]
          },
          {
            featureType: 'poi',
            elementType: 'all',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'road',
            elementType: 'all',
            stylers: [{ saturation: -100 }, { lightness: 45 }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.fill',
            stylers: [{ color: '#eeeeee' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#7b7b7b' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#ffffff' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'all',
            stylers: [{ visibility: 'simplified' }]
          },
          {
            featureType: 'road.arterial',
            elementType: 'labels.icon',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'transit',
            elementType: 'all',
            stylers: [{ visibility: 'off' }]
          },
          {
            featureType: 'water',
            elementType: 'all',
            stylers: [{ color: customTheme.primaryColor }, { visibility: 'on' }]
          }
        ]
      });

      // Add office marker
      const officeMarker = new window.google.maps.Marker({
        position: officeLocation,
        map: map,
        title: 'Ishaan Construction Works',
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <circle cx="20" cy="20" r="18" fill="${customTheme.primaryColor}" stroke="white" stroke-width="2"/>
              <path d="M20 8c-4.4 0-8 3.6-8 8 0 4.4 3.6 8 8 8s8-3.6 8-8c0-4.4-3.6-8-8-8zm0 12c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" fill="white"/>
            </svg>
          `)}`,
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20)
        }
      });

      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; font-family: Arial, sans-serif; max-width: 250px;">
            <h3 style="margin: 0 0 8px 0; color: ${customTheme.primaryColor}; font-size: 16px;">
              Ishaan Construction Works
            </h3>
            <p style="margin: 0 0 8px 0; color: #666; font-size: 14px;">
              ${officeLocation.address}
            </p>
            <div style="display: flex; gap: 8px; margin-top: 10px;">
              <button onclick="window.open('tel:8074635096')" style="
                background: ${customTheme.primaryColor}; 
                color: white; 
                border: none; 
                padding: 6px 12px; 
                border-radius: 4px; 
                cursor: pointer; 
                font-size: 12px;
              ">Call</button>
              <button onclick="window.open('https://wa.me/919972555661')" style="
                background: ${customTheme.accentColor}; 
                color: white; 
                border: none; 
                padding: 6px 12px; 
                border-radius: 4px; 
                cursor: pointer; 
                font-size: 12px;
              ">WhatsApp</button>
            </div>
          </div>
        `
      });

      officeMarker.addListener('click', () => {
        infoWindow.open(map, officeMarker);
      });

      setMapLoaded(true);
    };

    loadGoogleMaps();
  }, [customTheme.primaryColor]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          if (mapRef.current && window.google) {
            const map = new window.google.maps.Map(mapRef.current, {
              zoom: 15,
              center: userPos,
            });
            
            // Add user location marker
            new window.google.maps.Marker({
              position: userPos,
              map: map,
              title: 'Your Location',
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                  <svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="15" cy="15" r="12" fill="${customTheme.accentColor}" stroke="white" stroke-width="2"/>
                    <circle cx="15" cy="15" r="4" fill="white"/>
                  </svg>
                `)}`,
                scaledSize: new window.google.maps.Size(30, 30),
                anchor: new window.google.maps.Point(15, 15)
              }
            });
            
            // Add office marker
            new window.google.maps.Marker({
              position: officeLocation,
              map: map,
              title: 'Ishaan Construction Works',
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                  <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="18" fill="${customTheme.primaryColor}" stroke="white" stroke-width="2"/>
                    <path d="M20 8c-4.4 0-8 3.6-8 8 0 4.4 3.6 8 8 8s8-3.6 8-8c0-4.4-3.6-8-8-8zm0 12c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" fill="white"/>
                  </svg>
                `)}`,
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20)
              }
            });
          }
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const openDirections = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${officeLocation.lat},${officeLocation.lng}`;
    window.open(url, '_blank');
  };

  const findMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const url = `https://www.google.com/maps/dir/?api=1&destination=${officeLocation.lat},${officeLocation.lng}&origin=${latitude},${longitude}`;
          window.open(url, '_blank');
        },
        (error) => {
          console.error('Error getting location:', error);
          // Fallback to directions without origin
          openDirections();
        }
      );
    } else {
      // Fallback to directions without origin
      openDirections();
    }
  };

  const handleCall = (phoneNumber: string) => {
    window.open(`tel:${phoneNumber}`);
  };

  const handleWhatsApp = (phoneNumber: string) => {
    const message = 'Hi, I am interested in your construction services. Please provide more information.';
    window.open(`https://wa.me/91${phoneNumber}?text=${encodeURIComponent(message)}`);
  };

  return (
    <Card 
      sx={{ 
        height: 'auto',
        overflow: 'visible',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Map Container */}
        <Box
          ref={mapRef}
          sx={{
            width: '100%',
            height: 350,
            position: 'relative',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px 12px 0 0',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e0e0e0' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        >
          {/* Placeholder Map Content */}
          <Box textAlign="center" sx={{ p: 3 }}>
            <LocationOn sx={{ fontSize: 56, color: customTheme.primaryColor, mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
              Ishaan Construction Works
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, mx: 'auto' }}>
              {officeLocation.address}
            </Typography>
          </Box>
        </Box>
        
        {/* Contact Information Below Map */}
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom sx={{ mb: 2 }}>
            Contact Information
          </Typography>
          
          {/* Phone Numbers */}
          <Box display="flex" flexDirection="column" gap={2} mb={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Primary Contact
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<Phone />}
                  onClick={() => handleCall('8074635096')}
                  fullWidth
                  sx={{
                    background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                    minWidth: '120px',
                    height: '48px',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                    },
                  }}
                >
                  8074635096
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WhatsApp />}
                  onClick={() => handleWhatsApp('8074635096')}
                  fullWidth
                  sx={{
                    borderColor: customTheme.accentColor,
                    color: customTheme.accentColor,
                    minWidth: '120px',
                    height: '48px',
                    '&:hover': {
                      borderColor: customTheme.accentColor,
                      backgroundColor: customTheme.accentColor + '10',
                    },
                  }}
                >
                  WhatsApp
                </Button>
              </Box>
            </Box>
            
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Secondary Contact
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<Phone />}
                  onClick={() => handleCall('9972555661')}
                  fullWidth
                  sx={{
                    background: `linear-gradient(135deg, ${customTheme.primaryColor} 0%, ${customTheme.accentColor} 100%)`,
                    minWidth: '120px',
                    height: '48px',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${customTheme.primaryColor}dd 0%, ${customTheme.accentColor}dd 100%)`,
                    },
                  }}
                >
                  9972555661
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<WhatsApp />}
                  onClick={() => handleWhatsApp('9972555661')}
                  fullWidth
                  sx={{
                    borderColor: customTheme.accentColor,
                    color: customTheme.accentColor,
                    minWidth: '120px',
                    height: '48px',
                    '&:hover': {
                      borderColor: customTheme.accentColor,
                      backgroundColor: customTheme.accentColor + '10',
                    },
                  }}
                >
                  WhatsApp
                </Button>
              </Box>
            </Box>
          </Box>
          
          {/* Directions Button */}
          <Button
            variant="outlined"
            startIcon={<Directions />}
            onClick={openDirections}
            fullWidth
            sx={{
              borderColor: customTheme.primaryColor,
              color: customTheme.primaryColor,
              height: '48px',
              '&:hover': {
                borderColor: customTheme.primaryColor,
                backgroundColor: customTheme.primaryColor + '10',
              },
            }}
          >
            Get Directions
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Map;
